import { useMutation, useQuery } from "@apollo/client";
import {
  meGQL,
  SubscriptionPlan,
  updateUserMetadataGQL,
  User,
  UserMetadataInput,
} from "@onmo/graphql/graphql";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../constants/routes";

type Step = "idle" | "confirm" | "billing";

export const useSubscriptionFlow = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [step, setStep] = useState<Step>("idle");
  const isProcessingRef = useRef(false);

  const { data: meData } = useQuery<{ me: User }>(meGQL);
  const billingInfo = meData?.me?.metadata?.billingInfo;
  const hasBillingInfo = !!billingInfo?.name && !!billingInfo?.state;

  const [updateUserMetadata] = useMutation<
    { updateUserMetadata: { message: string } },
    { input: UserMetadataInput }
  >(updateUserMetadataGQL);

  const close = useCallback(() => {
    setStep("idle");
    setPlan(null);
    isProcessingRef.current = false;
  }, []);

  const start = useCallback((selectedPlan?: SubscriptionPlan | null) => {
    if (!selectedPlan) return;
    isProcessingRef.current = false;
    setPlan(selectedPlan);
    setStep("confirm");
  }, []);

  const goToPayment = useCallback(
    (planId: string) => {
      navigate(`${PageRoutes.Payment}?planId=${planId}`);
    },
    [navigate],
  );

  const confirm = useCallback(() => {
    if (!plan?.id || isProcessingRef.current) return;
    isProcessingRef.current = true;
    if (hasBillingInfo) {
      goToPayment(plan.id);
    } else {
      isProcessingRef.current = false;
      setStep("billing");
    }
  }, [plan?.id, hasBillingInfo, goToPayment]);

  const submitBilling = useCallback(
    async (data: { name: string; state: string }) => {
      if (!plan?.id) return;
      await updateUserMetadata({
        variables: { input: { billingInfo: data } },
        update: (cache) => {
          const existing = cache.readQuery<{ me: User }>({ query: meGQL });
          if (!existing?.me) return;
          cache.writeQuery({
            query: meGQL,
            data: {
              me: {
                ...existing.me,
                metadata: {
                  ...existing.me.metadata,
                  billingInfo: { __typename: "BillingInfo", ...data },
                },
              },
            },
          });
        },
      });
      goToPayment(plan.id);
    },
    [plan?.id, updateUserMetadata, goToPayment],
  );

  return {
    start,
    close,
    confirm,
    submitBilling,
    plan,
    isConfirmOpen: step === "confirm",
    isBillingOpen: step === "billing",
  };
};
